import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeftRight, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import { ShiftSwapRequestView } from '../../../../models/shiftSwap';
import { formatShiftSummary, formatLocalShiftSummary } from './format';

interface AcceptSwapDialogProps {
  swap: ShiftSwapRequestView | null;
  submitting: boolean;
  locale?: string;
  onClose: () => void;
  onConfirm: (swap: ShiftSwapRequestView) => void;
}

// Prefer the timezone-safe local wall-clock; fall back to the UTC field only when
// the server did not supply a local one.
const shiftSummary = (
  localStart: string | null | undefined,
  localEnd: string | null | undefined,
  utcStart: string | null | undefined,
  utcEnd: string | null | undefined,
  locale?: string,
): string => {
  if (localStart && localEnd) return formatLocalShiftSummary(localStart, localEnd, locale);
  if (utcStart && utcEnd) return formatShiftSummary(utcStart, utcEnd, locale);
  return '';
};

// Confirmation step for a counterparty accepting a swap directed at them. It spells
// out the exact shifts being exchanged, and warns loudly when acceptance applies the
// swap immediately (no manager approval).
const AcceptSwapDialog: React.FC<AcceptSwapDialogProps> = ({ swap, submitting, locale, onClose, onConfirm }) => {
  const { t } = useTranslation();

  const open = swap !== null;
  const autoApplies = !!swap?.autoAppliesOnAccept;
  const isOneWay = !!swap && (!swap.counterpartyShiftStartTime || !swap.counterpartyShiftEndTime);

  const takeSummary = swap
    ? shiftSummary(swap.requesterShiftLocalStart, swap.requesterShiftLocalEnd, swap.requesterShiftStartTime, swap.requesterShiftEndTime, locale)
    : '';
  const giveSummary = swap
    ? shiftSummary(swap.counterpartyShiftLocalStart, swap.counterpartyShiftLocalEnd, swap.counterpartyShiftStartTime, swap.counterpartyShiftEndTime, locale)
    : '';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogContent
        size="sm"
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('Accept this swap?')}</DialogTitle>
          <DialogDescription>
            {autoApplies
              ? t('Review the shifts you are about to exchange.')
              : t('Accepting sends this for manager approval.')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {swap && (
            <div className="space-y-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ArrowRight size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{t('You take')}</span>
                <span className="font-medium text-foreground tabular-nums">{takeSummary}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ArrowLeftRight size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{t('You give')}</span>
                <span className="font-medium text-foreground tabular-nums">
                  {isOneWay ? t('Nothing (one-way cover)') : giveSummary}
                </span>
              </div>
            </div>
          )}

          {autoApplies && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>{t('Accepting will immediately apply this swap and reassign the shift.')}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" size="sm" disabled={submitting} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="success"
            size="sm"
            loading={submitting}
            onClick={() => swap && onConfirm(swap)}
            data-testid="swap-accept-confirm"
          >
            {autoApplies ? t('Accept & apply') : t('Accept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptSwapDialog;
